import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../api";
import collingsLogo from "../../assets/collingslogo.png";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState(false);

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
    black: "#000000",
    white: "#FFFFFF",
    lightGray: "#F8F9FA",
    mediumGray: "#E9ECEF",
    darkGray: "#495057",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Simple validation
    if (!email) {
      setValidationError("Email is required.");
      toast.error("Email is required.");
      return;
    }
    // Simple email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidationError("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }
    setValidationError("");
    setLoading(true);
    try {
      await api.post("/role-management/forgot_password/", { email });
      toast.success(
        "If this email exists, a password reset link has been sent."
      );
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      let message = "Failed to send reset link.";
      if (error.response && error.response.data && error.response.data.detail) {
        message = error.response.data.detail;
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-12 w-full max-w-lg border border-gray-100">
        <div className="text-center mb-10">
          <div className="flex flex-col items-center justify-center mb-6">
            <div className="mb-4">
              <img
                src={collingsLogo}
                alt="Collings Real Estate"
                className="h-8 sm:h-9 w-auto max-w-[180px] object-contain mx-auto"
              />
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Zap
                className="w-4 h-4"
                style={{ color: brandColors.secondary }}
              />
              <span>AI-Powered Lead Generation Platform</span>
            </div>
          </div>
          <p className="text-gray-600 font-light text-lg">
            Forgot your password?
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {validationError && (
            <div
              className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-xl"
              role="alert"
            >
              <span className="block sm:inline">{validationError}</span>
            </div>
          )}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:border-collings-aquamarine text-sm transition-all duration-200 bg-gray-50 focus:bg-white"
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full text-white py-4 px-6 rounded-xl hover:opacity-90 transition-all duration-300 font-medium text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            style={{
              backgroundColor: brandColors.secondary,
              opacity: loading ? 0.6 : 1,
            }}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>

        <div className="mt-8 text-sm text-center">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-gray-900 font-medium"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
